import React, { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Truck, UploadCloud, Loader2, FileText, Trash2, CheckCircle2, ArrowRight,
  Database, FileSpreadsheet, Image as ImageIcon, AlertCircle, Info, LifeBuoy, Mail, Hash,
} from "lucide-react";
import ReviewCockpit from "@/components/migration/ReviewCockpit";

const SOURCE_TOOLS = [
  { id: "sevdesk", label: "sevDesk" },
  { id: "lexoffice", label: "Lexware Office / lexoffice" },
  { id: "fastbill", label: "FastBill" },
  { id: "papierkram", label: "Papierkram" },
  { id: "excel", label: "Excel / CSV" },
  { id: "other", label: "Anderes System" },
];

// Kurzanleitung je Quell-Tool: wo finde ich den passenden Export?
const EXPORT_HELP = {
  sevdesk: {
    title: "So exportierst du aus sevDesk",
    steps: [
      "Buchungen: Verwaltung → Export → „DATEV-Export“ → Zeitraum wählen → ZIP/EXTF herunterladen.",
      "Kontakte: Kontakte → Filter „alle“ → Aktionen → „Exportieren“ → CSV.",
      "Standardartikel: Stammdaten → Teile/Leistungen → „Exportieren“ → CSV.",
      "Belege (PDFs) sind im DATEV-ZIP meist enthalten — einfach das ganze ZIP hochladen.",
    ],
  },
  lexoffice: {
    title: "So exportierst du aus Lexware Office / lexoffice",
    steps: [
      "Buchungen: Mehr (•••) → „Datenexport“ → „DATEV-Format“ → Zeitraum → ZIP herunterladen.",
      "Kontakte: Kontakte → „Exportieren“ → CSV.",
      "Standardartikel: Artikel → „Exportieren“ → CSV.",
      "Das DATEV-ZIP inkl. Belegbilder kannst du komplett hier hochladen.",
    ],
  },
  fastbill: {
    title: "So exportierst du aus FastBill",
    steps: [
      "Einstellungen → Export → „DATEV (EXTF)“ → Zeitraum wählen → herunterladen.",
      "Kunden: Kunden → Export → CSV.",
    ],
  },
  papierkram: {
    title: "So exportierst du aus Papierkram",
    steps: [
      "Einstellungen → Export → „DATEV-Export“ → Zeitraum → ZIP herunterladen.",
      "Kontakte: Kontakte → Export → CSV.",
    ],
  },
  excel: {
    title: "Eigene Excel-/CSV-Datei",
    steps: [
      "Speichere deine Tabelle als CSV (Trennzeichen ; oder ,).",
      "Eine Spalte mit Name/E-Mail genügt für Kontakte; für Rechnungen bitte einen DATEV-EXTF-Export bevorzugen.",
      "Hinweis: Generische CSVs werden in einem späteren Schritt per KI zugeordnet.",
    ],
  },
  other: {
    title: "Anderes System",
    steps: [
      "Fast alle Buchhaltungstools können einen „DATEV-Export (EXTF/Buchungsstapel)“ erzeugen — das ist das zuverlässigste Format.",
      "Kontakte als CSV exportieren.",
      "Im Zweifel: alle Export-Dateien als ZIP hochladen, wir erkennen die Typen automatisch.",
    ],
  },
};

const TYPE_LABELS = {
  extf_csv: "DATEV-Buchungen", contacts_csv: "Kontakte", articles_csv: "Standardartikel",
  income_csv: "Einnahmen/Rechnungen", expense_csv: "Ausgaben/Belege", generic_csv: "CSV (generisch)",
  invoice_pdf: "Rechnung (PDF)", receipt_pdf: "Beleg (PDF)", asset_list_pdf: "Anlagenliste",
  bank_csv: "Bankauszug", image: "Bild", unknown: "Unbekannt",
};

const fileIcon = (t) =>
  t === "extf_csv" ? Database : t === "contacts_csv" ? FileSpreadsheet :
  t === "image" ? ImageIcon : FileText;

export default function Migration() {
  const [migration, setMigration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await api.get("/migration");
      setMigration(res.data);
    } catch (e) {
      if (e.response?.status === 404) setMigration(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;
  }

  const status = migration?.status;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="migration-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-11 w-11 rounded-xl bg-emerald-100 flex items-center justify-center">
          <Truck className="h-6 w-6 text-emerald-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Umzugs-Assistent</h1>
          <p className="text-sm text-slate-500">Hol deine Daten aus deinem alten Tool — sicher, geprüft, in einem Rutsch.</p>
        </div>
      </div>

      <StepIndicator status={status} />

      {(!migration || status === "draft") && <SetupStep busy={busy} setBusy={setBusy} onStarted={reload} />}
      {status === "uploading" && <UploadStep migration={migration} busy={busy} setBusy={setBusy} onReload={reload} />}
      {status === "analyzing" && <AnalyzingStep onReload={reload} />}
      {status === "review" && <ReviewCockpit migration={migration} onReload={reload} />}
      {status === "completed" && <ReportStep migration={migration} />}
    </div>
  );
}

const STEPS = [
  { key: "setup", label: "Quelle" },
  { key: "uploading", label: "Hochladen" },
  { key: "analyzing", label: "Analyse" },
  { key: "review", label: "Prüfen" },
  { key: "completed", label: "Fertig" },
];

function StepIndicator({ status }) {
  const idx = { undefined: 0, draft: 0, uploading: 1, analyzing: 2, review: 3, committing: 3, completed: 4 }[status] ?? 0;
  return (
    <div className="flex items-center gap-2 my-6" data-testid="migration-steps">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className={`flex items-center gap-2 ${i <= idx ? "text-emerald-700" : "text-slate-400"}`}>
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
              i < idx ? "bg-emerald-600 text-white" : i === idx ? "bg-emerald-100 border-2 border-emerald-600" : "bg-slate-100"}`}>
              {i < idx ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </div>
            <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < idx ? "bg-emerald-400" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Step 1: Setup ────────────────────────────────────────────────────────────
function SetupStep({ busy, setBusy, onStarted }) {
  const year = new Date().getFullYear();
  const [sourceTool, setSourceTool] = useState("sevdesk");
  const [fromYear, setFromYear] = useState(year - 1);
  const [toYear, setToYear] = useState(year);
  const [checklist, setChecklist] = useState({ contacts: true, invoices: true, expenses: true, articles: true });

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/migration/start", {
        source_tool: sourceTool,
        checklist: { ...checklist, assets: false },
        import_scope: { from_year: Number(fromYear), to_year: Number(toYear), archive_older: true },
      });
      toast.success("Migration gestartet");
      onStarted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Start fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="setup-step">
      <CardHeader><CardTitle className="text-lg">Aus welchem Tool ziehst du um?</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {SOURCE_TOOLS.map((t) => (
            <button
              key={t.id}
              onClick={() => setSourceTool(t.id)}
              data-testid={`source-${t.id}`}
              className={`rounded-xl border-2 p-4 text-left transition-all ${
                sourceTool === t.id ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
            >
              <Database className={`h-5 w-5 mb-2 ${sourceTool === t.id ? "text-emerald-600" : "text-slate-400"}`} />
              <div className="font-medium text-sm text-slate-800">{t.label}</div>
            </button>
          ))}
        </div>

        {EXPORT_HELP[sourceTool] && (
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4" data-testid="export-help">
            <div className="flex items-center gap-2 mb-2 text-blue-800 font-medium text-sm">
              <Info className="h-4 w-4" /> {EXPORT_HELP[sourceTool].title}
            </div>
            <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
              {EXPORT_HELP[sourceTool].steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>
        )}

        <div>
          <Label className="text-sm">Buchhalterischer Zeitraum</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input type="number" min="2000" max="2100" value={fromYear} onChange={(e) => setFromYear(e.target.value)} className="w-28" data-testid="scope-from" />
            <span className="text-slate-400">bis</span>
            <Input type="number" min="2000" max="2100" value={toYear} onChange={(e) => setToYear(e.target.value)} className="w-28" data-testid="scope-to" />
            <span className="text-xs text-slate-500">Ältere Belege → nur Archiv (GoBD-Tresor)</span>
          </div>
        </div>

        <div>
          <Label className="text-sm">Was möchtest du übernehmen?</Label>
          <div className="flex flex-wrap gap-4 mt-2">
            {[["contacts", "Kunden/Kontakte"], ["invoices", "Ausgangsrechnungen"], ["expenses", "Ausgaben/Belege"], ["articles", "Standardartikel"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={checklist[k]} onCheckedChange={(c) => setChecklist({ ...checklist, [k]: !!c })} data-testid={`checklist-${k}`} />
                {lbl}
              </label>
            ))}
          </div>
        </div>

        <Button onClick={start} disabled={busy} className="w-full sm:w-auto" data-testid="start-migration-btn">
          {busy ? "Startet…" : <>Migration starten <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Step 2: Upload ───────────────────────────────────────────────────────────
function UploadStep({ migration, busy, setBusy, onReload }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total, percent }
  const [dragOver, setDragOver] = useState(false);
  const [aiConsent, setAiConsent] = useState(!!migration?.ai_consent);
  const [showBelegConfirm, setShowBelegConfirm] = useState(false);

  const loadFiles = useCallback(async () => {
    const res = await api.get("/migration/files");
    setFiles(res.data);
  }, []);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const onPick = async (fileList) => {
    if (!fileList?.length || uploading) return;   // Doppel-Trigger / parallele Uploads verhindern
    const arr = Array.from(fileList);
    setUploading(true);
    setUploadProgress({ current: 0, total: arr.length, percent: 0 });
    let failed = 0;
    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      setUploadProgress({ current: i + 1, total: arr.length, percent: 0 });
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/migration/files", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setUploadProgress({ current: i + 1, total: arr.length, percent: pct });
          },
        });
        await loadFiles();   // live: jede Datei sofort in der Liste anzeigen
      } catch (e) {
        failed++;
        toast.error(`${f.name}: ${e.response?.data?.detail || "Upload fehlgeschlagen"}`);
      }
    }
    await loadFiles();        // Endabgleich – auch wenn einzelne Dateien fehlschlugen
    setUploading(false);
    setUploadProgress(null);
    if (failed === 0) toast.success(arr.length === 1 ? "Datei hochgeladen" : `${arr.length} Dateien hochgeladen`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const dropped = e.dataTransfer?.files;
    if (dropped?.length) onPick(dropped);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const overrideType = async (id, t) => {
    await api.patch(`/migration/files/${id}`, { detected_type: t });
    loadFiles();
  };
  const removeFile = async (id) => {
    await api.delete(`/migration/files/${id}`);
    loadFiles();
  };

  const analyze = async (consent = aiConsent) => {
    setBusy(true);
    try {
      await api.post("/migration/analyze", { ai_consent: consent });
      // WICHTIG: onReload AWAITEN — so bleibt der Button bis zum Step-Wechsel auf
      // 'Analysiert…' (disabled). Sonst wird busy sofort zurückgesetzt, der Upload-
      // Step bleibt sichtbar (GET /migration ggf. verzögert) und der Nutzer klickt
      // ein zweites Mal. (Bug Juni 2026: "Analyse starten muss 2x geklickt werden")
      await onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const usable = files.filter((f) => f.status !== "ignored");
  // Beleg-PDFs/Bilder benötigen die KI-Zustimmung — sonst werden sie NICHT erfasst.
  const belegFiles = usable.filter((f) => ["receipt_pdf", "invoice_pdf", "image"].includes(f.detected_type));
  const summaryByType = usable.reduce((acc, f) => { acc[f.detected_type] = (acc[f.detected_type] || 0) + 1; return acc; }, {});
  const summaryEntries = Object.entries(summaryByType).sort((a, b) => b[1] - a[1]);

  const startAnalyze = () => {
    if (belegFiles.length > 0 && !aiConsent) { setShowBelegConfirm(true); return; }
    analyze();
  };

  return (
    <Card data-testid="upload-step">
      <CardHeader><CardTitle className="text-lg">Exportdateien hochladen</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <label
          className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 cursor-pointer transition-colors ${
            dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:bg-slate-50"}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          data-testid="upload-dropzone"
        >
          {uploading ? <Loader2 className="h-8 w-8 animate-spin text-emerald-600" /> : <UploadCloud className={`h-8 w-8 ${dragOver ? "text-emerald-600" : "text-slate-400"}`} />}
          <span className="mt-2 text-sm text-slate-600">{uploading ? "Lädt hoch…" : (dragOver ? "Zum Hochladen loslassen" : "CSV / EXTF / ZIP / PDF hierher ziehen oder klicken")}</span>
          <input type="file" multiple className="hidden" onChange={(e) => onPick(e.target.files)} />
        </label>

        {files.length > 0 && (
          <div className="space-y-2" data-testid="file-list">
            {files.map((f) => {
              const Icon = fileIcon(f.detected_type);
              const ignored = f.status === "ignored";
              return (
                <div key={f.id} className={`flex items-center gap-3 rounded-lg border p-2.5 ${ignored ? "opacity-50 bg-slate-50" : "bg-white"}`} data-testid={`file-row-${f.id}`}>
                  <Icon className="h-5 w-5 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{f.original_name}</div>
                    <div className="text-xs text-slate-500">{(f.size_bytes / 1024).toFixed(0)} KB {ignored && "· Duplikat ignoriert"}</div>
                  </div>
                  <Select value={f.detected_type} onValueChange={(t) => overrideType(f.id, t)}>
                    <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABELS).map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-600" data-testid={`file-delete-${f.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {usable.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="upload-summary">
            <div className="flex items-center gap-2 mb-2 text-slate-700 font-medium text-sm">
              <Info className="h-4 w-4" /> Übersicht: das wird analysiert &amp; übernommen
            </div>
            <div className="flex flex-wrap gap-2">
              {summaryEntries.map(([type, count]) => (
                <span key={type} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600" data-testid={`summary-${type}`}>
                  {TYPE_LABELS[type] || type}: <strong className="text-slate-800">{count}</strong>
                </span>
              ))}
            </div>
            {belegFiles.length > 0 && (
              <p className="mt-2 text-xs text-slate-500" data-testid="summary-beleg-note">
                Davon <strong>{belegFiles.length}</strong> Beleg-PDF/Bild — diese werden nur <strong>mit KI-Zustimmung</strong> als Ausgabe erfasst.
              </p>
            )}
          </div>
        )}

        {belegFiles.length > 0 && !aiConsent && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="beleg-consent-warning">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm leading-relaxed text-amber-800">
              Du hast <strong>{belegFiles.length} Beleg-/PDF-Datei(en)</strong> hochgeladen. Ohne die
              KI-Zustimmung unten werden diese <strong>nicht automatisch als Ausgabe erfasst</strong>.
              Aktiviere die Zustimmung, damit deine Belege ausgelesen werden.
            </p>
          </div>
        )}

        <div className={`flex items-start gap-3 rounded-xl border-2 p-4 transition-colors ${
          belegFiles.length > 0 && !aiConsent ? "border-emerald-400 bg-emerald-50/70 ring-2 ring-emerald-200" : "border-slate-200 bg-slate-50"}`} data-testid="ai-consent-box">
          <Checkbox
            id="ai-consent"
            checked={aiConsent}
            onCheckedChange={(c) => setAiConsent(!!c)}
            className="mt-0.5"
            data-testid="ai-consent-checkbox"
          />
          <Label htmlFor="ai-consent" className="text-sm font-normal leading-relaxed text-slate-600 cursor-pointer">
            <span className="block font-semibold text-slate-900 mb-0.5">KI-Belegerkennung aktivieren {belegFiles.length > 0 && <span className="text-emerald-700">(für deine {belegFiles.length} Belege empfohlen)</span>}</span>
            Ich stimme zu, dass meine hochgeladenen <strong>Belege und PDF-/Bild-Dateien</strong> zur
            automatischen Erkennung von einer KI (Anthropic Claude) verarbeitet werden.
            <span className="block mt-1 text-xs text-slate-400">
              Ohne Zustimmung werden nur strukturierte Dateien (CSV, DATEV/EXTF, Kontakte) automatisch
              verarbeitet — Belege/PDFs musst du anschließend manuell erfassen.
            </span>
          </Label>
        </div>

        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-slate-500">{usable.length} Datei(en) bereit</span>
          <Button onClick={startAnalyze} disabled={busy || usable.length === 0} data-testid="analyze-btn">
            {busy ? "Analysiert…" : <>Analyse starten <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>
        </div>
      </CardContent>

      <Dialog open={showBelegConfirm} onOpenChange={setShowBelegConfirm}>
        <DialogContent data-testid="beleg-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Belege ohne KI-Zustimmung?</DialogTitle>
            <DialogDescription>
              Du hast {belegFiles.length} Beleg-/PDF-Datei(en) hochgeladen. Ohne KI-Zustimmung werden diese
              <strong> nicht ausgelesen</strong> und erscheinen nicht in deinen Ausgaben. Wie möchtest du fortfahren?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" disabled={busy} onClick={() => { setShowBelegConfirm(false); analyze(false); }} data-testid="beleg-confirm-skip">
              Ohne Belege fortfahren
            </Button>
            <Button disabled={busy} onClick={() => { setAiConsent(true); setShowBelegConfirm(false); analyze(true); }} data-testid="beleg-confirm-enable-ai">
              KI aktivieren &amp; Belege auslesen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Step 3: Analyzing ────────────────────────────────────────────────────────
function AnalyzingStep({ onReload }) {
  const [prog, setProg] = useState(null);
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const res = await api.get("/migration/status");
        setProg(res.data.progress || null);
        if (res.data.status !== "analyzing") onReload();
      } catch (err) { console.error("status poll failed", err); }
    }, 2000);
    return () => clearInterval(t);
  }, [onReload]);

  const total = prog?.files_total || 0;
  const processed = prog?.files_processed || 0;
  const ok = prog?.files_ok || 0;
  const failed = prog?.files_failed || 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  return (
    <Card data-testid="analyzing-step">
      <CardContent className="py-14 flex flex-col items-center">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
        <p className="mt-4 font-medium text-slate-700">Deine Daten werden analysiert…</p>
        <p className="text-sm text-slate-500">Buchungen, Belege und Kontakte werden erkannt und geprüft.</p>
        {total > 0 && (
          <div className="mt-6 w-full max-w-md" data-testid="analyze-progress">
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-slate-600" data-testid="analyze-processed">{processed}/{total} Dateien ausgelesen</span>
              <span className="flex gap-3">
                <span className="font-medium text-emerald-600" data-testid="analyze-ok">{ok} erkannt</span>
                {failed > 0 && <span className="font-medium text-red-500" data-testid="analyze-failed">{failed} fehlgeschlagen</span>}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Step 5: Report ───────────────────────────────────────────────────────────
function ReportStep({ migration }) {
  const r = migration.report || {};
  const c = r.committed || {};
  const [supportOpen, setSupportOpen] = useState(false);
  const proposal = migration.number_sequence_proposal || {};
  const [nrAccepted, setNrAccepted] = useState(!!proposal.accepted);
  const [adopting, setAdopting] = useState(false);

  const adoptNumberRange = async () => {
    setAdopting(true);
    try {
      const res = await api.post("/migration/number-range/adopt");
      toast.success(`Nummernkreis übernommen — nächste Rechnung: ${res.data.preview}`);
      setNrAccepted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen");
    } finally { setAdopting(false); }
  };

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/migration/report.pdf`;
    const token = localStorage.getItem("auth_token") || "";
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.blob() : Promise.reject(res.statusText)))
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = "Migrations-Abschlussbericht.pdf";
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch((err) => toast.error("PDF-Download fehlgeschlagen"));
  };

  const downloadBelegeZip = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/migration/export.zip`;
    const token = localStorage.getItem("auth_token") || "";
    toast.info("ZIP wird erstellt…");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.blob() : Promise.reject(res.statusText)))
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = "ivoliq-belege-nach-status.zip";
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("ZIP-Download fehlgeschlagen"));
  };

  return (
    <div className="space-y-4" data-testid="report-step">
      <Card>
        <CardContent className="py-10">
          <div className="flex flex-col items-center text-center">
            <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Migration abgeschlossen 🎉</h2>
            <p className="text-sm text-slate-500 mb-6">Deine Daten sind jetzt in ivoliq.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full max-w-2xl">
              {[["Kunden", c.customers || 0], ["Rechnungen", c.invoices || 0], ["Ausgaben", c.expenses || 0], ["Artikel", c.articles || 0]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-slate-50 p-4">
                  <div className="text-2xl font-bold text-emerald-700">{v}</div>
                  <div className="text-xs text-slate-500">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-sm text-slate-600 space-y-1">
              <div>Umsatz importiert: <span className="font-semibold">{(r.sum_revenue || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span></div>
              <div>Ausgaben importiert: <span className="font-semibold">{(r.sum_expenses || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span></div>
              {r.skipped_duplicates > 0 && <div className="text-amber-600">{r.skipped_duplicates} Duplikat(e) übersprungen</div>}
              {r.skipped_files > 0 && (
                <div className="text-amber-600" data-testid="report-skipped-files">
                  {r.skipped_files} beschädigte/nicht lesbare Datei(en) übersprungen
                </div>
              )}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button onClick={downloadPdf} data-testid="download-report-pdf-btn">
                <FileText className="h-4 w-4 mr-2" /> Abschlussbericht (PDF)
              </Button>
              <Button variant="outline" onClick={downloadBelegeZip} data-testid="download-belege-zip-btn">
                <Database className="h-4 w-4 mr-2" /> Belege als ZIP (nach Status sortiert)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Nummernkreis aus altem Tool übernehmen und fortsetzen */}
      {proposal.proposed_next && (
        <Card className="border-emerald-200 bg-emerald-50/60" data-testid="adopt-number-range-card">
          <CardContent className="py-5">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                <Hash className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-slate-900">Rechnungsnummern lückenlos fortsetzen?</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  Aus deinem alten Tool haben wir den Nummernkreis erkannt (zuletzt
                  {" "}<span className="font-mono font-semibold">{proposal.highest_found}</span>).
                  Deine nächste Rechnung in ivoliq wäre dann
                  {" "}<span className="font-mono font-semibold">{proposal.proposed_next}</span> — so bleiben deine
                  Rechnungsnummern GoBD-konform ohne Lücke.
                </p>
                {nrAccepted ? (
                  <p className="mt-3 text-sm font-medium text-emerald-700 flex items-center gap-1.5" data-testid="adopt-number-range-done">
                    <CheckCircle2 className="h-4 w-4" /> Nummernkreis übernommen — du kannst ihn in den Einstellungen anpassen.
                  </p>
                ) : (
                  <Button size="sm" onClick={adoptNumberRange} disabled={adopting}
                    className="mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid="adopt-number-range-btn">
                    <Hash className="h-4 w-4 mr-2" /> {adopting ? "Übernimmt…" : "Nummernkreis übernehmen und fortsetzen"}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Support-Hinweis nach abgeschlossener Migration */}
      <Card className="border-amber-200 bg-amber-50/60" data-testid="report-support-hint">
        <CardContent className="py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <LifeBuoy className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-slate-900">Stimmt etwas nicht?</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                Deine Daten sind jetzt fest in deinem Konto verbucht. Aus <strong>GoBD- und Sicherheitsgründen</strong>
                {" "}kann ein erneuter Import nicht automatisch ausgelöst werden — sonst würden Duplikate entstehen.
                Schreib uns kurz, <strong>was nicht passt und warum ein erneuter Import nötig ist</strong>. Wir setzen
                den Import dann sicher für dich zurück.
              </p>
              <Button variant="outline" size="sm" onClick={() => setSupportOpen(true)}
                className="mt-3 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="report-support-contact-btn">
                <Mail className="h-4 w-4 mr-2" /> Support kontaktieren
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <SupportContactDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}

// ── Support-Kontakt-Dialog (sendet via Brevo, DSGVO-Zustimmung erforderlich) ──
function SupportContactDialog({ open, onClose }) {
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [shareFiles, setShareFiles] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!consent) { toast.error("Bitte bestätige, dass wir dich kontaktieren dürfen."); return; }
    if (message.trim().length < 10) { toast.error("Bitte beschreibe dein Anliegen etwas genauer."); return; }
    setSending(true);
    try {
      const res = await api.post("/support/contact", {
        subject: "Migrations-Support – erneuter Import nötig",
        message: message.trim(),
        consent_to_contact: true,
        category: "migration_rollback",
        share_migration_files: shareFiles,
        page_url: window.location.href,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      });
      const ticket = res.data?.ticket_number;
      toast.success(ticket
        ? `Anfrage gesendet · Ticket ${ticket} — Bestätigung per E-Mail unterwegs.`
        : "Anfrage gesendet — wir melden uns bei dir.");
      setMessage(""); setConsent(false); setShareFiles(false);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="support-contact-dialog">
        <DialogHeader>
          <DialogTitle>Support kontaktieren</DialogTitle>
          <DialogDescription>
            Beschreibe kurz, was nicht passt und warum ein erneuter Import nötig ist.
            Bitte gib im Text die nötigen Details an — wir melden uns dann bei dir.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            placeholder="z.B. Bei mehreren Rechnungen fehlt die Mehrwertsteuer — ein erneuter Import wäre nötig, weil…"
            data-testid="support-message-input"
          />
          <div className="flex items-start gap-2.5">
            <Checkbox id="support-consent" checked={consent} onCheckedChange={(c) => setConsent(!!c)} className="mt-0.5" data-testid="support-consent-checkbox" />
            <Label htmlFor="support-consent" className="text-sm font-normal leading-relaxed text-slate-600 cursor-pointer">
              Ich stimme zu, dass ivoliq mich per E-Mail zu diesem Anliegen <strong>direkt kontaktieren</strong> darf.
            </Label>
          </div>
          <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <Checkbox id="support-share-files" checked={shareFiles} onCheckedChange={(c) => setShareFiles(!!c)} className="mt-0.5" data-testid="support-share-files-checkbox" />
            <Label htmlFor="support-share-files" className="text-sm font-normal leading-relaxed text-slate-600 cursor-pointer">
              <strong>Optional:</strong> Meine hochgeladenen Importdateien zur Fehleranalyse beifügen.
              So können wir nachvollziehen, was schiefgelaufen ist. Die Dateien werden nach Abschluss
              deiner Anfrage automatisch gelöscht.
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={sending || !consent} data-testid="support-send-btn">
            {sending ? "Sendet…" : "Anfrage senden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
